// packages/front/src/portfolio/modals/add-cashflow-modal.tsx

import { CASHFLOW_REASON_OBJ, CURRENCY_LIST } from "@signals/shared";
import { useEffect, useMemo, useState, type FC, type FormEvent } from "react";
import { Button, Modal, Select } from "../../_common";
import {
  useCreateCashFlow,
  usePortfolioAccounts,
  type CashFlow,
  type CreateCashFlowInput,
} from "../portfolio.hooks";
import { apiErrorMessage } from "../portfolio-api-error";
import { accountUnavailableMessage, operationAccountOptions } from "../portfolio-account-availability";

export interface AddCashFlowModalProps {
  open: boolean;
  onClose: () => void;
  portfolioId?: string;
  initialData?: Partial<CashFlow> | undefined;
}

const REASONS = [
  "deposit",
  "withdrawal",
  "commission",
  "tax",
  "transfer",
  "trade",
  "other",
];

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const numberClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm [appearance:textfield]";
const labelClass = "text-sm font-medium";

export const AddCashFlowModal: FC<AddCashFlowModalProps> = ({
  open,
  onClose,
  portfolioId,
  initialData,
}) => {
  const accountsQuery = usePortfolioAccounts(portfolioId);
  const accounts = accountsQuery.data?.items ?? [];
  const createCashFlow = useCreateCashFlow();
  const saving = createCashFlow.isPending;

  const [accountId, setAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [reason, setReason] = useState("deposit");
  const [formError, setFormError] = useState<string | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");

  // Seed form from initialData when opening (pre-fill active account).
  useEffect(() => {
    if (!open || !initialData) return;
    if (initialData.accountId !== undefined)
      setAccountId(initialData.accountId);
    if (initialData.currency !== undefined) setCurrency(initialData.currency);
  }, [open, initialData]);

  const accountCurrency = useMemo(() => {
    const acc = accounts.find((a) => a.id === accountId);
    return acc?.currency ?? null;
  }, [accounts, accountId]);

  // Default currency from account when currency is empty.
  useEffect(() => {
    if (!accountCurrency) return;
    if (!currency) setCurrency(accountCurrency);
  }, [accountCurrency, currency]);

  // A closed account accepts operations only up to its closure date; the receiving account of a
  // transfer is checked against the same date.
  const dates = useMemo(() => [date], [date]);
  const accountOptions = useMemo(
    () => operationAccountOptions(accounts, dates, [accountId, initialData?.accountId]),
    [accounts, dates, accountId, initialData?.accountId],
  );
  const toAccountOptions = useMemo(
    () => operationAccountOptions(accounts.filter((a) => a.id !== accountId), dates, [toAccountId]),
    [accounts, dates, accountId, toAccountId],
  );
  const accountError = useMemo(
    () =>
      accountUnavailableMessage(accounts.find((a) => a.id === accountId), dates) ??
      (reason === CASHFLOW_REASON_OBJ.TRANSFER
        ? accountUnavailableMessage(accounts.find((a) => a.id === toAccountId), dates)
        : null),
    [accounts, accountId, toAccountId, reason, dates],
  );

  const signHint = useMemo(() => {
    const hints: Record<string, string> = {
      deposit: "Deposit should be positive (money in).",
      withdrawal: "Withdrawal should be negative (money out).",
      commission: "Commission should be negative (expense).",
      tax: "Tax withheld is negative (expense); a tax refund is positive.",
      transfer:
        "Transfer: source account is debited (negative), target is credited (positive).",
      trade: "Trade corrections can be positive or negative.",
      other: "Amount can be positive or negative.",
    };
    return hints[reason] ?? "Use negative value for expenses.";
  }, [reason]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      setFormError("Select account");
      return;
    }
    if (accountError) {
      setFormError(accountError);
      return;
    }
    if (!currency) {
      setFormError("Select currency");
      return;
    }
    if (reason === CASHFLOW_REASON_OBJ.TRANSFER && !toAccountId) {
      setFormError("Select receiving account");
      return;
    }
    setFormError(null);
    const payload: CreateCashFlowInput = {
      accountId,
      amount: Number(amount) || 0,
      currency: currency || "USD",
      reason,
      date,
      note,
    };
    if (reason === CASHFLOW_REASON_OBJ.TRANSFER && toAccountId) {
      payload.toAccountId = toAccountId;
    }
    try {
      await createCashFlow.mutateAsync(payload);
      onClose();
    } catch (error) {
      setFormError(apiErrorMessage(error, "Failed to create cash flow"));
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Cash Flow">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {formError && (
          <p role="alert" className="text-sm text-destructive">
            {formError}
          </p>
        )}
        <label className={labelClass}>Account</label>
        <Select
          className={inputClass}
          value={accountId}
          onChange={(value) => {
            setAccountId(value);
            setFormError(null);
          }}
          disabled={Boolean(initialData?.accountId)}
          placeholder={
            accounts.length === 0
              ? "No accounts yet — create one first"
              : "Select account"
          }
          options={accountOptions}
          ariaLabel="Account"
        />
        {accountError && (
          <p role="alert" className="text-sm text-destructive">
            {accountError}
          </p>
        )}

        <label className={labelClass}>Amount</label>
        <input
          className={numberClass}
          type="number"
          step="any"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground">{signHint}</p>

        <label className={labelClass}>Currency</label>
        <Select
          className={inputClass}
          value={currency}
          onChange={(value) => {
            setCurrency(value);
            setFormError(null);
          }}
          placeholder="Select currency"
          options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
          ariaLabel="Currency"
        />

        <label className={labelClass}>Reason</label>
        <Select
          className={inputClass}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setFormError(null);
          }}
          options={REASONS.map((r) => ({ value: r, label: r }))}
          ariaLabel="Reason"
        />

        {reason === CASHFLOW_REASON_OBJ.TRANSFER && (
          <>
            <label className={labelClass}>To Account</label>
            <Select
              className={inputClass}
              value={toAccountId}
              onChange={(value) => {
                setToAccountId(value);
                setFormError(null);
              }}
              placeholder="Select receiving account"
              options={toAccountOptions}
              ariaLabel="To account"
            />
          </>
        )}

        <label className={labelClass}>Date</label>
        <input
          className={inputClass}
          type="date"
          data-testid="cashflow-date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <label className={labelClass}>Note</label>
        <input
          className={inputClass}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || accountError !== null}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
